import { Icon } from '@gftg/ui';
import { useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ErrorBlock, ICON_BUTTON, LoadingBlock } from '../components/states';
import { TOTAL_STEPS, useProfileEditor } from '../lib/profileEditor';
import {
  useProfileOptions,
  type CertificationOption,
  type MyProfile,
  type Option,
  type ProfileUpdate,
} from '../lib/queries';

const FIELD =
  'rounded-button text-row w-full min-h-touch border border-border-input bg-surface px-4 py-3 font-semibold text-ink placeholder:font-normal placeholder:text-ink-4';
const EYEBROW = 'text-eyebrow tracking-eyebrow font-bold text-ink-3 uppercase';
const HINT = 'text-subtitle text-ink-3';

/**
 * The guided setup of PRF-08: five steps, under three minutes, skippable and resumable. Every answer
 * goes to the server as it is given (see useProfileEditor), so leaving mid-step loses nothing and
 * "Continue" is navigation rather than a save — including on a different device tomorrow.
 */
export function QuestionnairePage() {
  const navigate = useNavigate();
  const options = useProfileOptions();
  const editor = useProfileEditor();
  const [params] = useSearchParams();

  // `?step=5` is how the profile screen links straight at the privacy switches. Without it the
  // questionnaire resumes where it was abandoned, which is only knowable once the profile arrives —
  // hence null until then rather than 0, which would silently restart her.
  const [step, setStep] = useState<number | null>(() => {
    const asked = Number(params.get('step'));
    return Number.isInteger(asked) && asked >= 1 && asked <= TOTAL_STEPS ? asked - 1 : null;
  });

  if (options.isPending || editor.isPending) {
    return <LoadingBlock label="Loading the questionnaire…" />;
  }

  if (options.isError || !options.data || editor.isError || !editor.profile) {
    return (
      <ErrorBlock
        message="The questionnaire could not be loaded."
        onRetry={() => {
          void options.refetch();
          editor.refetch();
        }}
      />
    );
  }

  const profile = editor.profile;
  const patch = editor.patch;
  const current = step ?? Math.min(profile.questionnaireStep, TOTAL_STEPS - 1);

  const back = () => {
    if (current === 0) {
      void navigate('/account');
      return;
    }
    setStep(current - 1);
  };

  const forward = () => {
    if (current === TOTAL_STEPS - 1) {
      patch({ questionnaireStep: TOTAL_STEPS, questionnaireCompleted: true });
      void navigate('/account');
      return;
    }

    const next = current + 1;
    setStep(next);
    // The furthest step reached, so a return trip resumes rather than restarts. Stepping back to
    // re-read an answer must not drag the resume point back with it.
    patch({ questionnaireStep: Math.max(profile.questionnaireStep, next) });
  };

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="px-gutter pt-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={back} aria-label="Back" className={ICON_BUTTON}>
            <Icon name="chevron-left" size={18} />
          </button>
          <p className="text-subtitle flex-1 font-medium text-ink-2">
            Step {current + 1} of {TOTAL_STEPS}
          </p>
          <SaveState isSaving={editor.isSaving} failed={editor.saveFailed} />
        </div>

        <ol className="mt-4 flex gap-1.5" aria-hidden="true">
          {Array.from({ length: TOTAL_STEPS }, (_, index) => (
            <li
              key={index}
              className={`h-[3px] flex-1 rounded-full ${index <= current ? 'bg-brand' : 'bg-sand-3'}`}
            />
          ))}
        </ol>
      </header>

      <main className="px-gutter flex-1 pt-5 pb-6">
        {current === 0 && <StepRegistration profile={profile} patch={patch} />}
        {current === 1 && (
          <StepBusiness
            profile={profile}
            patch={patch}
            industries={options.data.industries}
            interests={options.data.interests}
            maxInterests={options.data.maxInterests}
          />
        )}
        {current === 2 && (
          <StepCertifications
            profile={profile}
            patch={patch}
            certifications={options.data.certifications}
          />
        )}
        {current === 3 && <StepGoals profile={profile} patch={patch} goals={options.data.goals} />}
        {current === 4 && <StepVisibility profile={profile} patch={patch} />}
      </main>

      <footer className="px-gutter sticky bottom-0 flex gap-card border-t border-border-2 bg-canvas py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {current > 0 && (
          <button
            type="button"
            onClick={back}
            className="text-button rounded-button flex min-h-touch flex-1 items-center justify-center border border-border-input bg-surface px-4 font-semibold text-ink"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={forward}
          className="text-button rounded-button flex min-h-touch flex-[2] items-center justify-center bg-ink px-4 font-semibold text-white"
        >
          {current === TOTAL_STEPS - 1 ? 'Finish' : 'Continue'}
        </button>
      </footer>
    </div>
  );
}

/**
 * Nothing here has a Save button, so the only way she can tell her answers are kept is if the screen
 * says so. It replaces the time estimate once there is something to report, because a woman
 * mid-questionnaire has stopped caring how long it takes and started caring whether it stuck.
 */
function SaveState({ isSaving, failed }: { isSaving: boolean; failed: boolean }) {
  if (failed) {
    return (
      <p className="text-subtitle font-medium text-alert-text" role="status">
        Not saved — retrying
      </p>
    );
  }

  return (
    <p className="text-subtitle text-ink-3" role="status">
      {isSaving ? 'Saving…' : '≈2 min'}
    </p>
  );
}

type Patch = (changes: ProfileUpdate) => void;

interface StepProps {
  profile: MyProfile;
  patch: Patch;
}

function StepRegistration({ profile, patch }: StepProps) {
  return (
    <Step
      title="Check what we already have"
      lede="This came from your Eventbrite order. Fix anything that is wrong — nothing here is public except name, company and title."
    >
      {/* Two fields rather than the design's one. DATA-02 identifies a woman by her address and her
          two names, and splitting one box on the last space would rename "Ana Maria Lopez Garcia"
          every time she saved. */}
      <Field label="Name">
        <input
          className={FIELD}
          value={profile.firstName}
          onChange={(event) => patch({ firstName: event.target.value })}
          placeholder="First name"
          autoComplete="given-name"
          aria-label="First name"
        />
        <input
          className={`${FIELD} mt-card`}
          value={profile.lastName}
          onChange={(event) => patch({ lastName: event.target.value })}
          placeholder="Last name"
          autoComplete="family-name"
          aria-label="Last name"
        />
      </Field>

      <Field label="Company and title">
        <input
          className={FIELD}
          value={profile.company ?? ''}
          onChange={(event) => patch({ company: event.target.value })}
          placeholder="Company"
          autoComplete="organization"
          aria-label="Company"
        />
        <input
          className={`${FIELD} mt-card`}
          value={profile.jobTitle ?? ''}
          onChange={(event) => patch({ jobTitle: event.target.value })}
          placeholder="Title"
          autoComplete="organization-title"
          aria-label="Title"
        />
      </Field>

      <Field label="LinkedIn">
        <input
          className={FIELD}
          value={profile.linkedinUrl ?? ''}
          onChange={(event) => patch({ linkedinUrl: event.target.value })}
          placeholder="linkedin.com/in/yourname"
          inputMode="url"
          autoCapitalize="off"
          aria-label="LinkedIn"
        />
      </Field>

      <p className={HINT}>
        Most attendees look you up here first. If you would rather not share it, the rest of this
        profile stands on its own.
      </p>
    </Step>
  );
}

function StepBusiness({
  profile,
  patch,
  industries,
  interests,
  maxInterests,
}: StepProps & { industries: Option[]; interests: Option[]; maxInterests: number }) {
  const atCap = profile.interestIds.length >= maxInterests;

  return (
    <Step
      title="Tell us about your business"
      lede="This is what the matching engine reads. Two minutes now saves you an hour of small talk in October."
    >
      <Field label="Industry">
        <select
          className={FIELD}
          value={profile.industryId ?? ''}
          onChange={(event) => patch({ industryId: event.target.value || null })}
          aria-label="Industry"
        >
          <option value="">Choose an industry</option>
          {industries.map((industry) => (
            <option key={industry.id} value={industry.id}>
              {industry.label}
            </option>
          ))}
        </select>
      </Field>

      <div>
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <p className={EYEBROW}>Interests</p>
          <p className="text-subtitle text-ink-3">
            {profile.interestIds.length} of {maxInterests} selected
          </p>
        </div>

        <div className="flex flex-wrap gap-2" role="group" aria-label="Interests">
          {/* PRF-03 lets organizers edit this list, which means they can also empty it. Saying so
              is the difference between a step to skip and a step that looks broken. */}
          {interests.length === 0 && (
            <p className={HINT}>
              The organizers have not published the topic list yet. Skip this for now and come back
              later.
            </p>
          )}
          {interests.map((interest) => {
            const selected = profile.interestIds.includes(interest.id);
            return (
              <Chip
                key={interest.id}
                label={interest.label}
                selected={selected}
                // At the cap the unpicked chips stop responding rather than silently dropping
                // somebody's earlier choice.
                disabled={!selected && atCap}
                onClick={() =>
                  patch({
                    interestIds: selected
                      ? profile.interestIds.filter((id) => id !== interest.id)
                      : [...profile.interestIds, interest.id],
                  })
                }
              />
            );
          })}
        </div>

        {interests.length > 0 && (
          <p className={`${HINT} mt-3`}>
            Pick up to {maxInterests}. Fewer, sharper choices give you better matches than ticking
            everything.
          </p>
        )}
      </div>
    </Step>
  );
}

function StepCertifications({
  profile,
  patch,
  certifications,
}: StepProps & { certifications: CertificationOption[] }) {
  const noneOption = certifications.find((option) => /^none/i.test(option.label));

  const toggle = (id: string) => {
    const selected = profile.certificationIds.includes(id);

    if (selected) {
      patch({ certificationIds: profile.certificationIds.filter((item) => item !== id) });
      return;
    }

    // "None yet" and a certification cannot both be true, so picking one clears the other.
    patch({
      certificationIds:
        noneOption && id === noneOption.id
          ? [id]
          : [...profile.certificationIds.filter((item) => item !== noneOption?.id), id],
    });
  };

  return (
    <Step
      title="Certifications"
      lede="Procurement officers filter the directory by these first. Check everything you hold."
    >
      {certifications.length === 0 && (
        <p className={HINT}>
          The organizers have not published the certification list yet. Nothing here is required.
        </p>
      )}

      <ul className="space-y-card">
        {certifications.map((option) => {
          const selected = profile.certificationIds.includes(option.id);
          return (
            <li key={option.id}>
              <button
                type="button"
                onClick={() => toggle(option.id)}
                aria-pressed={selected}
                className={`rounded-card flex w-full items-center gap-3 border p-4 text-left ${
                  selected ? 'border-brand bg-brand-wash' : 'border-border bg-surface'
                }`}
              >
                <span
                  className={`rounded-well flex size-6 shrink-0 items-center justify-center border ${
                    selected ? 'border-brand bg-brand text-white' : 'border-border-input bg-surface'
                  }`}
                  aria-hidden="true"
                >
                  {selected && <Icon name="check" size={14} />}
                </span>
                <span className="min-w-0">
                  <span className="text-row block font-semibold text-ink">{option.label}</span>
                  {option.sublabel && (
                    <span className="text-subtitle mt-0.5 block text-ink-3">{option.sublabel}</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* PRF-02 lists the capability statement, but file storage is not wired yet, so the row
          states what it will do instead of offering a picker that goes nowhere. */}
      <div className="rounded-card border border-dashed border-border-dashed bg-surface p-4">
        <p className="text-row font-semibold text-ink-4">Capability statement (PDF)</p>
        <p className={`${HINT} mt-0.5`}>
          Optional, shown on your directory profile. Uploads arrive with your account.
        </p>
      </div>
    </Step>
  );
}

function StepGoals({ profile, patch, goals }: StepProps & { goals: Option[] }) {
  return (
    <Step
      title="What would make this trip worth it?"
      lede="Your matches are written against this answer, so be specific."
    >
      <div
        className="flex flex-wrap gap-2"
        role="group"
        aria-label="What would make this trip worth it"
      >
        {goals.length === 0 && (
          <p className={HINT}>
            The organizers have not published this list yet. The box below is enough on its own.
          </p>
        )}
        {goals.map((goal) => {
          const selected = profile.goalIds.includes(goal.id);
          return (
            <Chip
              key={goal.id}
              label={goal.label}
              selected={selected}
              onClick={() =>
                patch({
                  goalIds: selected
                    ? profile.goalIds.filter((id) => id !== goal.id)
                    : [...profile.goalIds, goal.id],
                })
              }
            />
          );
        })}
      </div>

      <Field label="In your own words">
        <textarea
          className={`${FIELD} font-normal`}
          rows={4}
          value={profile.freeTextAsk ?? ''}
          onChange={(event) => patch({ freeTextAsk: event.target.value })}
          placeholder="I want two conversations with buyers of facilities services in Florida."
          aria-label="In your own words"
        />
      </Field>
    </Step>
  );
}

function StepVisibility({ profile, patch }: StepProps) {
  return (
    <Step
      title="Who can see you"
      lede="Your email and phone are never shown to other attendees, whatever you choose here."
    >
      <Toggle
        label="Show me in the directory"
        note="Name, company, title, LinkedIn and certifications"
        checked={profile.directoryVisible}
        onChange={(checked) => patch({ directoryVisible: checked })}
      />

      <Toggle
        label="Include me in Concierge Connections"
        note="Organizers pair attendees and publish the matches to both of you"
        checked={profile.matchingOptIn}
        onChange={(checked) => patch({ matchingOptIn: checked })}
      />

      {/*
        The prototype had a third switch for sharing your email with accepted connections. The
        client's Aug 19 decision removed contact exchange from this release, and the database has
        deliberately no column that could expose an address — so this is a statement, not a switch.
      */}
      <div className="rounded-card border border-border bg-sand p-4">
        <p className="text-row font-semibold text-ink">Contact details stay with the organizers</p>
        <p className={`${HINT} mt-0.5`}>
          Email and phone are never visible to other attendees, and there is no setting that can
          change that.
        </p>
      </div>

      <div className="rounded-card flex items-center gap-3 border border-dashed border-border-dashed bg-surface p-4">
        <span
          className="text-subtitle flex size-12 shrink-0 items-center justify-center rounded-full border border-dashed border-border-dashed text-ink-4"
          aria-hidden="true"
        >
          photo
        </span>
        <p className={HINT}>
          A photo is optional, and it is the one thing that makes people walk up to you. Uploads
          arrive with your account.
        </p>
      </div>
    </Step>
  );
}

function Step({ title, lede, children }: { title: string; lede: string; children: ReactNode }) {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-session-title font-semibold text-ink">{title}</h1>
        <p className="text-body mt-2 text-ink-3">{lede}</p>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className={`${EYEBROW} mb-2`}>{label}</p>
      {children}
    </div>
  );
}

function Chip({
  label,
  selected,
  disabled = false,
  onClick,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`text-subtitle flex min-h-touch items-center rounded-full border px-4 font-medium ${
        selected
          ? 'border-brand bg-brand text-white'
          : disabled
            ? 'border-border-2 bg-surface text-ink-4'
            : 'border-border-input bg-surface text-ink'
      }`}
    >
      {label}
    </button>
  );
}

function Toggle({
  label,
  note,
  checked,
  onChange,
}: {
  label: string;
  note: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="rounded-card flex items-center gap-3 border border-border bg-surface p-4">
      <span className="min-w-0 flex-1">
        <span className="text-row block font-semibold text-ink">{label}</span>
        <span className={`${HINT} mt-0.5 block`}>{note}</span>
      </span>

      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand ${
          checked ? 'bg-brand' : 'bg-sand-3'
        }`}
        aria-hidden="true"
      >
        <span
          className={`absolute top-1 size-5 rounded-full bg-white transition-all ${
            checked ? 'left-6' : 'left-1'
          }`}
        />
      </span>
    </label>
  );
}
